/**
 * Extrai as perguntas que a IA fez ao gestor numa mensagem persistida.
 *
 * Lê `message.tool_calls`, encontra a entrada `name === 'perguntar_ao_gestor'` e devolve
 * `output.perguntas`. Mesma regra do `servicos-citados.js`: uma extração para os dois
 * caminhos (ao vivo e reaberta), sem coluna nova no banco.
 *
 * Mensagens anteriores à feature não têm `output` — devolvem lista vazia, sem quebrar.
 *
 * @param {Object} message Mensagem no shape que o backend persiste (`ToolCallRegistrado`).
 * @returns {Array} Itens `{ pergunta, porque? }`, ou `[]`.
 */
export function extrairPerguntasAoGestor(message) {
  if (!message || !message.tool_calls || !message.tool_calls.length) return []
  for (const chamada of message.tool_calls) {
    if (
      chamada &&
      chamada.name === 'perguntar_ao_gestor' &&
      chamada.output &&
      chamada.output.perguntas
    ) {
      return chamada.output.perguntas
    }
  }
  return []
}

/**
 * ★ ESPELHO MANUAL do backend: `core/entrega/material.py`, `PREFIXO_RESPOSTAS` (:25)
 * e o regex `_BLOCO` (:41).
 *
 * O backend reconhece as respostas do gestor por DUAS coisas, e as duas precisam
 * bater: a mensagem tem de COMEÇAR com este prefixo (`startswith`, :68) e os pares
 * têm de vir em blocos numerados `N. pergunta\nResposta: texto`. Sem isso, os pares
 * não são extraídos e o rascunho de entrega sai GENÉRICO — sem erro, sem vermelho,
 * sem teste falhando. É a falha mais cara desta cadeia porque não faz barulho.
 */
export const PREFIXO_RESPOSTAS =
  'Respondendo às perguntas em aberto do parecer:'

/**
 * Monta o texto da próxima mensagem do gestor a partir do que ele respondeu.
 *
 * O backend NÃO tem endpoint para as respostas: o canal é o próprio turno seguinte,
 * e o formato acima é o contrato. O pareamento é montado aqui — cada bloco carrega a
 * pergunta e a resposta, para o modelo não ter de reconciliar por ordem.
 *
 * Campo vazio é OMITIDO em vez de virar "(sem resposta)": pergunta não respondida
 * continua em aberto, e mandar um vazio faria o modelo tratá-la como resolvida. A
 * numeração acompanha os blocos EMITIDOS (1..N), não o índice da pergunta na tela —
 * é o que o `_BLOCO` do backend espera.
 *
 * @param {Array} perguntas Itens `{ pergunta }` na ordem em que foram exibidos.
 * @param {Object} respostas Mapa `indice -> texto` do que o gestor escreveu.
 * @param {String=} prefixo A primeira linha da mensagem. Default: o do PARECER.
 * @returns {String} Texto pareado com o prefixo, ou `''` quando nada foi respondido.
 *
 * ⚠️ **O prefixo é parâmetro porque ele é a ETIQUETA de quem perguntou, e o
 * backend colhe por ela.** `core/entrega/material.py::_respostas_do_gestor`
 * casa só por `startswith(PREFIXO_RESPOSTAS)`, sem conferir de que turno a
 * mensagem veio — então qualquer fluxo que reusasse este util com o default
 * teria as respostas dele colhidas como respostas ao PARECER, no rascunho de
 * entrega daquela conversa. Aconteceu com o onboarding, que não tem parecer
 * nenhum: além do dado indo para o lugar errado (mudo), a bolha do usuário
 * mostrava "Respondendo às perguntas em aberto do parecer:" no primeiro
 * contato. Reusar o FORMATO é o certo; herdar a copy do parecer, não.
 */
export function montarRespostasPareadas(perguntas, respostas, prefixo) {
  const blocos = []
  ;(perguntas || []).forEach(function (item, indice) {
    const resposta = String((respostas || {})[indice] || '').trim()
    if (!resposta) return
    const numero = blocos.length + 1
    blocos.push(numero + '. ' + item.pergunta + '\nResposta: ' + resposta)
  })
  if (!blocos.length) return ''
  // ⚠️ O FORMATO dos blocos é contrato e nunca muda; o PREFIXO é de quem
  // pergunta. Ver a nota do parâmetro no docblock.
  return (prefixo || PREFIXO_RESPOSTAS) + '\n\n' + blocos.join('\n\n')
}

/**
 * Quebra o corpo (o que vem depois do prefixo) nos blocos numerados.
 *
 * Split por lookahead em vez de um regex global com lookahead de fim: a resposta vem
 * de textarea e PODE ter linha em branco no meio, então o bloco só termina no próximo
 * `N. ` ou no fim do texto — nunca na primeira linha vazia. Mesma regra do backend.
 */
function blocosDoCorpo(corpo) {
  return String(corpo || '')
    .split(/\n(?=\d+\.\s)/)
    .map(function (bruto) {
      const achado = /^\d+\.\s+([\s\S]*?)\nResposta:[ \t]*([\s\S]*)$/.exec(
        bruto.trim()
      )
      if (!achado) return null
      return { pergunta: achado[1].trim(), resposta: achado[2].trim() }
    })
    .filter(Boolean)
}

/**
 * Lê de volta o texto pareado e devolve a resposta de cada pergunta.
 *
 * Mesmo formato que `montarRespostasPareadas` escreve, que é o do backend. Ler de
 * volta é o que permite exibir as respostas DENTRO do bloco depois de recarregar a
 * conversa: elas não têm coluna própria no banco, moram na mensagem seguinte do gestor.
 *
 * Exige o prefixo, como o backend: mensagem comum do gestor que por acaso comece com
 * um número não pode ser confundida com um formulário respondido.
 *
 * Casa por TEXTO da pergunta, não por ordem — pergunta deixada em branco não é
 * enviada, então a n-ésima resposta não corresponde à n-ésima pergunta.
 *
 * @param {String} texto Conteúdo da mensagem `user` que respondeu ao formulário.
 * @param {Array} perguntas Itens `{ pergunta }` do formulário.
 * @param {String=} prefixo A etiqueta de quem perguntou — a MESMA passada a
 *   `montarRespostasPareadas`. Default: o do PARECER. O onboarding lê com a
 *   dele (`PREFIXO_RESPOSTAS_DO_ONBOARDING`): as respostas da etapa 1 moram
 *   na mensagem seguinte, como as do parecer, e é assim que o cartão as
 *   mostra de volta depois de um F5 e no aside ([owner, 21/09/2026]).
 * @returns {Array} Respostas alinhadas por índice com `perguntas` (`''` quando não
 *   respondida), ou `[]` quando o texto não é uma resposta deste formulário.
 */
export function extrairRespostasPareadas(texto, perguntas, prefixo) {
  const conteudo = String(texto || '')
  const etiqueta = prefixo || PREFIXO_RESPOSTAS
  if (!conteudo || !perguntas || !perguntas.length) return []
  if (!conteudo.startsWith(etiqueta)) return []

  const porPergunta = {}
  blocosDoCorpo(conteudo.slice(etiqueta.length)).forEach(function (
    par
  ) {
    porPergunta[par.pergunta] = par.resposta
  })

  let encontrouAlguma = false
  const respostas = perguntas.map(function (item) {
    const resposta = porPergunta[item.pergunta] || ''
    if (resposta) encontrouAlguma = true
    return resposta
  })
  return encontrouAlguma ? respostas : []
}

/** Palavras sem carga semântica — presentes em quase toda pergunta. */
const VAZIAS = new Set([
  'qual',
  'quais',
  'quanto',
  'quantos',
  'quantas',
  'como',
  'onde',
  'quem',
  'que',
  'de',
  'do',
  'da',
  'dos',
  'das',
  'em',
  'no',
  'na',
  'nos',
  'nas',
  'o',
  'a',
  'os',
  'as',
  'um',
  'uma',
  'e',
  'ou',
  'por',
  'para',
  'com',
  'sem',
  'ja',
  'esta',
  'estao',
  'ser',
  'sera',
  'seu',
  'sua',
  'voce',
  'hoje',
])

/** Minúsculas, sem acento e sem pontuação, quebrado em palavras com carga. */
function palavras(texto) {
  const limpo = String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .replaceAll(/[^a-z0-9\s]/g, ' ')
  return limpo.split(/\s+/).filter(function (palavra) {
    return palavra.length > 2 && !VAZIAS.has(palavra)
  })
}

/**
 * Quantas palavras com carga as duas perguntas compartilham, e o quanto isso
 * representa da MENOR das duas.
 *
 * Contenção, não Jaccard: aqui uma pergunta é a elaboração da outra, não um par
 * simétrico. O formulário reescreve a do painel com exemplos e qualificadores
 * ("...por faixa de patrimônio" vira "...por faixa de patrimônio (ex.: até R$
 * 500 mil, ...)"), e cada palavra a mais derrubava o Jaccard: medido nas duas
 * perguntas reais de um turno, 0.40 — abaixo de qualquer limiar seguro — ainda
 * que uma contenha a outra inteira. Pela menor, a mesma dupla dá 0.67.
 */
function sobreposicao(a, b) {
  const A = new Set(palavras(a))
  const B = new Set(palavras(b))
  if (!A.size || !B.size) return { comuns: 0, proporcao: 0 }
  let comuns = 0
  A.forEach(function (palavra) {
    if (B.has(palavra)) comuns++
  })
  return { comuns: comuns, proporcao: comuns / Math.min(A.size, B.size) }
}

/**
 * Limiares de duplicata. Os dois precisam bater: a proporção sozinha marcaria
 * como repetida uma pergunta curta e genérica que por acaso cabe dentro de uma
 * longa. Exigir um piso ABSOLUTO de palavras em comum evita isso.
 *
 * Deliberadamente conservador: esconder uma pergunta que não era duplicata
 * (o gestor deixa de ver algo em aberto) custa mais que mostrar uma repetida.
 */
const MIN_PALAVRAS_EM_COMUM = 3
const MIN_PROPORCAO = 0.6

/**
 * Tira da lista do painel as perguntas que o formulário já faz.
 *
 * As duas listas saem de chamadas diferentes do modelo — o parecer consolidado
 * do painel e a tool `perguntar_ao_gestor` —, então o texto nunca bate exato,
 * mas repete o miolo ("ROA comissionado por faixa de patrimônio"). Comparar por
 * palavra resolve sem depender do modelo se policiar.
 *
 * @param {Array} doPainel `parecer.perguntas_em_aberto` — `list[str]`.
 * @param {Array} doFormulario Itens `{ pergunta }` da tool.
 * @returns {Array} As do painel que o formulário NÃO cobre.
 */
export function perguntasNaoCobertas(doPainel, doFormulario) {
  const perguntadas = (doFormulario || []).map(function (item) {
    return item.pergunta
  })
  return (doPainel || []).filter(function (texto) {
    return !perguntadas.some(function (jaPerguntada) {
      const medida = sobreposicao(texto, jaPerguntada)
      return (
        medida.comuns >= MIN_PALAVRAS_EM_COMUM &&
        medida.proporcao >= MIN_PROPORCAO
      )
    })
  })
}
